// Downloads a signed upload as a Blob, reporting progress as it streams in
// (Living Room "Downloading": file cards and the photo viewer).
export async function fetchWithProgress(
  url: string,
  options: {
    signal?: AbortSignal;
    /** Used when the server sends no Content-Length. */
    sizeHint?: number;
    onProgress?: (loaded: number, total: number) => void;
  } = {}
): Promise<Blob> {
  // Without credentials (#76): a signed URL needs none (M16), and browsers
  // keep such requests on a connection of their own. Over HTTP/2 everything
  // else to twinden.com shares one connection, which a photo going up (XHR,
  // credentialed) fills on a phone's uplink, so a picture opened meanwhile
  // waited for the whole upload.
  const response = await fetch(url, {
    signal: options.signal,
    credentials: 'omit',
  });
  if (!response.ok || !response.body)
    throw new Error(`HTTP ${response.status}`);
  const total =
    Number(response.headers.get('content-length')) || options.sizeHint || 0;
  const reader = response.body.getReader();
  const chunks: BlobPart[] = [];
  let loaded = 0;
  options.onProgress?.(0, total);
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    options.onProgress?.(loaded, total);
  }
  return new Blob(chunks, {
    type: response.headers.get('content-type') ?? undefined,
  });
}

/** Hands a Blob to the browser to save under a name. */
export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
